/**
 * v3.19.0 — Documents-First Dropzone for Client Profile KB cards.
 *
 * Replaces the legacy "+ Add" button on the four card types
 * (past-performances / certifications / key-personnel / winning-proposals).
 * User drags or selects one+ files → backend creates stub records,
 * attaches files, and runs background AI extraction. Status badges on
 * rows reflect "Extracting…" → "Review extracted metadata" / "Failed".
 *
 * Props:
 *   - entityType: one of the 4 slug strings above
 *   - organizationId: client org owning the records
 *   - onUploaded(createdRecords): called after the POST returns, used by
 *     the parent to merge the new stubs and start its polling cycle.
 *   - compact: render as a thin inline strip (default) vs. a tall card
 *     when the card body is empty.
 *   - testIdPrefix: scopes data-testid for tests.
 */
import { useCallback, useRef, useState } from 'react';
import { Upload, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { describeApiError, readFetchError } from '../../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACCEPT_EXT = '.pdf,.docx,.doc,.txt,.rtf';
const MAX_FILES = 10;
const MAX_FILE_MB = 50;

export default function KbDocumentsDropzone({
  entityType,
  organizationId,
  onUploaded,
  compact = true,
  testIdPrefix = 'kb-dropzone',
  label,
}) {
  const { authToken } = useAuth();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onPick = () => inputRef.current?.click();

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      if (files.length > MAX_FILES) {
        toast.error(`Max ${MAX_FILES} files per upload — got ${files.length}.`);
        return;
      }
      const oversize = files.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
      if (oversize) {
        toast.error(`${oversize.name} is over the ${MAX_FILE_MB} MB cap.`);
        return;
      }
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      setUploading(true);
      try {
        const url = `${API_URL}/api/knowledge-base/${entityType}/stub-from-files?organization_id=${encodeURIComponent(
          organizationId,
        )}`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: fd,
        });
        if (!res.ok) {
          const msg = await readFetchError(res, `HTTP ${res.status}`);
          throw new Error(msg);
        }
        const body = await res.json();
        toast.success(
          `${body.created.length} ${
            body.created.length === 1 ? 'record' : 'records'
          } added — AI is extracting metadata in the background.`,
          { duration: 5000 },
        );
        onUploaded?.(body.created || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[kb-doc-first upload]', e);
        toast.error(describeApiError(e, 'Upload failed'), { duration: 8000 });
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [authToken, entityType, organizationId, onUploaded],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    handleFiles(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <button
      type="button"
      onClick={onPick}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      disabled={uploading}
      data-testid={`${testIdPrefix}-${entityType}`}
      className={cn(
        'group relative w-full rounded-lg border-2 border-dashed transition-all',
        'flex items-center justify-center gap-2 text-sm font-medium',
        compact ? 'px-3 py-2' : 'px-6 py-8 flex-col',
        dragOver
          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
          : 'border-slate-300 bg-slate-50/50 hover:border-emerald-400 hover:bg-emerald-50/50 text-slate-600 hover:text-emerald-700',
        uploading && 'opacity-70 cursor-wait',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_EXT}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testIdPrefix}-${entityType}-input`}
      />
      {uploading ? (
        <>
          <Loader2 className={cn('animate-spin', compact ? 'w-4 h-4' : 'w-6 h-6')} />
          <span>Uploading…</span>
        </>
      ) : (
        <>
          {compact ? (
            <Upload className="w-4 h-4" />
          ) : (
            <FileText className="w-7 h-7 text-emerald-500" />
          )}
          <span className={compact ? '' : 'mt-1 text-base'}>
            {label ||
              (compact
                ? 'Drop files or click to upload — AI will fill in details'
                : 'Drop one or more files here, or click to select')}
          </span>
          {!compact && (
            <span className="text-xs text-slate-500 mt-1">
              PDF, DOCX, or TXT · up to {MAX_FILES} files · {MAX_FILE_MB} MB each
            </span>
          )}
        </>
      )}
    </button>
  );
}
